import React, { Suspense, lazy, useEffect, useId, useMemo, useState } from 'react'

import { formatValue, niceTicks } from './ChartView'
import { formatNumber, formatTick, unitOf } from './chartFormat'
import { wordsFor } from './chartWords'

const StateMap = lazy(() => import('./StateMap'))

/**
 * A ranking chart: many things (states, airports, colleges) compared on one
 * measure, drawn as horizontal bars sorted highest first so every label is
 * readable, even on a phone. The value axis includes zero. `average_label`
 * names the point that is a benchmark ("U.S. overall") rather than one of the
 * things ranked; it is drawn as a dashed line across the bars.
 *
 * On phones, and for very long rankings (every county), it opens on the top
 * and bottom 10 with a button to show the rest. A chart of U.S. states can also
 * be shown as a map; the bars stay the default. A point with its own page
 * (`href`, a company's food recalls) links its label there.
 *
 *   <RankingView chart={{ title, x_label, y_label, data, average_label }} />
 */

const SEARCHABLE_POINTS = 60 // keep in step with Chart::SEARCHABLE_POINTS
const ENDS = 10 // how many of the top and of the bottom a collapsed ranking shows

export const STATE_NAMES = new Set([
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
])

/** The state a label names: "Texas", or "Texas: Liam" for a chart of each state's top name. */
export function stateOf(label) {
  const name = String(label).split(':')[0].trim()
  return STATE_NAMES.has(name) ? name : null
}

/**
 * The points ranked highest first (ties keep their order and share a rank),
 * and the benchmark point, which isn't ranked.
 */
export function rankPoints(data, averageLabel) {
  const points = (Array.isArray(data) ? data : []).filter((p) => p && Number.isFinite(Number(p.value)))
  const average = averageLabel ? points.find((p) => p.label === averageLabel) ?? null : null
  const sorted = points.filter((p) => p !== average).sort((a, b) => Number(b.value) - Number(a.value))
  const ranked = []
  sorted.forEach((p, i) => {
    const tied = i > 0 && Number(p.value) === Number(sorted[i - 1].value)
    ranked.push({ ...p, value: Number(p.value), rank: tied ? ranked[i - 1].rank : i + 1 })
  })
  return { ranked, average: average ? { ...average, value: Number(average.value) } : null }
}

function useNarrow() {
  const query = '(max-width: 639px)'
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches)
  useEffect(() => {
    const media = window.matchMedia?.(query)
    if (!media) return undefined
    const update = () => setNarrow(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return narrow
}

// Inside an embed's iframe a link opens the page in the reader's window, not the frame.
const linkTarget = () => (typeof window !== 'undefined' && window.self !== window.top ? '_top' : undefined)

const margin = (p) => (p.margin != null && Number.isFinite(Number(p.margin)) ? Number(p.margin) : null)

export function RankingView({ chart }) {
  const narrow = useNarrow()
  const [view, setView] = useState('bars')
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const searchId = useId()

  const { ranked, average } = useMemo(() => rankPoints(chart?.data, chart?.average_label), [chart?.data, chart?.average_label])
  const isStates = ranked.length >= 40 && ranked.every((p) => stateOf(p.label))
  const searchable = ranked.length > SEARCHABLE_POINTS

  const locale = chart?.locale ?? null
  const words = wordsFor(chart)
  const count = (value) => formatValue(value, locale)

  if (ranked.length === 0) {
    return <p className="text-sm text-ink-muted">{words.noData}</p>
  }

  // The scale covers every value, margin of error and the benchmark, and zero.
  const values = [0, ...(average ? [average.value] : [])]
  ranked.forEach((p) => {
    const m = margin(p)
    values.push(p.value, ...(m == null ? [] : [p.value - m, p.value + m]))
  })
  const ticks = niceTicks(Math.min(...values), Math.max(...values), narrow ? 3 : 5)
  const lo = ticks[0]
  const hi = ticks[ticks.length - 1]
  const unit = unitOf(chart.y_label)
  const number = (value) => formatNumber(value, unit, locale)
  // On a phone, label every other grid line so the numbers don't collide.
  const labelEvery = narrow && ticks.length > 4 ? 2 : 1
  const pct = (v) => ((v - lo) / (hi - lo || 1)) * 100

  const needle = query.trim().toLowerCase()
  const matches = needle ? ranked.filter((p) => String(p.label).toLowerCase().includes(needle)) : null
  const collapsed = !matches && !expanded && ranked.length > 2 * ENDS + 2 && (narrow || searchable)
  const rows = matches ?? (collapsed ? [...ranked.slice(0, ENDS), null, ...ranked.slice(-ENDS)] : ranked)

  const longest = Math.max(...ranked.map((p) => String(p.label).length + String(p.rank).length + 1))
  const labelWidth = narrow ? '42%' : `${Math.min(Math.max(longest, 8), 30) + 1}ch`
  const grid = { gridTemplateColumns: `${labelWidth} minmax(0, 1fr) auto` }

  const toggle = isStates ? (
    <div className="inline-flex gap-1 rounded-lg border border-line p-1 mb-4" role="group" aria-label={words.showAs}>
      {[['bars', words.bars], ['map', words.map]].map(([key, name]) => (
        <button
          key={key}
          type="button"
          aria-pressed={view === key}
          onClick={() => setView(key)}
          className={`${view === key ? 'btn-primary' : 'btn-ghost'} btn-sm min-h-10`}
        >
          {name}
        </button>
      ))}
    </div>
  ) : null

  const averageNote = average ? (
    <p className="text-xs text-ink-muted mb-3 flex items-center gap-2">
      <svg width="12" height="16" className="shrink-0" aria-hidden="true">
        <line x1="6" x2="6" y1="0" y2="16" stroke="var(--color-chart-reference)" strokeWidth="2" strokeDasharray="4 3" />
      </svg>
      <span>{average.label}: <span className="font-semibold text-ink">{number(average.value)}</span>{margin(average) != null ? ` ±${number(margin(average))}` : ''}</span>
    </p>
  ) : null

  if (view === 'map') {
    return (
      <div className="w-full">
        {toggle}
        {chart.y_label ? <p className="text-xs font-medium text-ink-muted mb-2">{chart.y_label}</p> : null}
        <Suspense fallback={<p className="text-sm text-ink-muted">{words.loadingMap}</p>}>
          <StateMap chart={chart} ranked={ranked} average={average} />
        </Suspense>
      </div>
    )
  }

  const search = searchable ? (
    <div className="mb-4">
      <label htmlFor={searchId} className="form-label">{words.find(chart.x_label)}</label>
      <input
        id={searchId}
        type="search"
        className="input-form-field"
        placeholder={words.placeholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <p className="form-hint" aria-live="polite">
        {matches ? words.matching(count(matches.length), count(ranked.length)) : words.inAll(count(ranked.length))}
      </p>
    </div>
  ) : null

  const hidden = ranked.length - 2 * ENDS

  return (
    <div className="w-full">
      {toggle}
      {search}
      {chart.y_label ? <p className="text-xs font-medium text-ink-muted mb-1">{chart.y_label}</p> : null}
      {averageNote}
      {matches && matches.length === 0 ? (
        <p className="text-sm text-ink-muted">{words.nothing(query.trim())}</p>
      ) : (
        <div className="grid gap-x-3 text-sm" style={grid} role="table" aria-label={chart.title || words.ranking}>
          <div role="row" className="contents">
            <span role="columnheader" className="sr-only">{words.rankAnd(chart.x_label)}</span>
            <div role="columnheader" className="relative h-5 text-xs text-ink-muted" style={{ gridRow: 1, gridColumn: 2 }} aria-hidden="true">
              {ticks.map((t, i) => (i % labelEvery !== 0 ? null : (
                <span
                  key={t}
                  className="absolute top-0 tabular-nums whitespace-nowrap"
                  style={{ left: `${pct(t)}%`, transform: i === 0 ? 'none' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)' }}
                >
                  {formatTick(t, unit, ticks[1] - ticks[0], locale)}
                </span>
              )))}
            </div>
            <span role="columnheader" className="sr-only">{chart.y_label || words.value}</span>
          </div>

          {/* Grid lines, the zero line and the benchmark, behind every row. */}
          <div className="relative pointer-events-none" style={{ gridColumn: 2, gridRow: `2 / span ${rows.length}` }} aria-hidden="true">
            {ticks.map((t) => (
              <span key={t} className="absolute inset-y-0 border-l border-chart-grid" style={{ left: `${pct(t)}%`, opacity: t === 0 ? 1 : 0.6 }} />
            ))}
            {average ? (
              <span className="absolute inset-y-0 z-10 border-l-2 border-dashed border-chart-reference" style={{ left: `${pct(average.value)}%`, marginLeft: -1 }} />
            ) : null}
          </div>

          {rows.map((p, i) => (p === null ? (
            <div key="gap" role="row" className="relative z-20 py-1" style={{ gridColumn: '1 / -1', gridRow: i + 2 }}>
              <button type="button" className="btn-secondary btn-sm w-full min-h-10" onClick={() => setExpanded(true)}>
                {words.showAll(count(ranked.length), count(hidden))}
              </button>
            </div>
          ) : (
            <div key={`${p.label}-${i}`} role="row" className="contents">
              <span role="cell" className="flex min-w-0 items-center gap-1.5 py-1" style={{ gridRow: i + 2, gridColumn: 1 }} title={p.label}>
                <span className="shrink-0 w-[3ch] text-right text-xs tabular-nums text-ink-muted">{p.rank}</span>
                {p.href ? (
                  <a href={p.href} target={linkTarget()} className="truncate table-link">{p.label}</a>
                ) : (
                  <span className="truncate text-ink">{p.label}</span>
                )}
              </span>
              <span className="relative block h-full min-h-7" style={{ gridRow: i + 2, gridColumn: 2 }} aria-hidden="true">
                <span
                  className={`absolute top-1/2 h-4 -translate-y-1/2 bg-chart-series ${p.value < 0 ? 'rounded-l' : 'rounded-r'}`}
                  style={{ left: `${pct(Math.min(0, p.value))}%`, width: `${Math.abs(pct(p.value) - pct(0))}%` }}
                />
                {margin(p) ? (
                  <span
                    className="absolute top-1/2 h-2 -translate-y-1/2 border-x border-chart-reference/60"
                    style={{ left: `${pct(p.value - margin(p))}%`, width: `${pct(p.value + margin(p)) - pct(p.value - margin(p))}%` }}
                  >
                    <span className="absolute inset-x-0 top-1/2 border-t border-chart-reference/60" />
                  </span>
                ) : null}
              </span>
              <span role="cell" className="self-center text-right tabular-nums text-ink whitespace-nowrap" style={{ gridRow: i + 2, gridColumn: 3 }}>
                {number(p.value)}
                {margin(p) != null ? <span className="text-xs text-ink-muted"> ±{number(margin(p))}</span> : null}
              </span>
            </div>
          )))}
        </div>
      )}
      {expanded && !matches && (narrow || searchable) ? (
        <button type="button" className="btn-ghost btn-sm mt-3 min-h-10" onClick={() => setExpanded(false)}>
          {words.showEnds(ENDS)}
        </button>
      ) : null}
    </div>
  )
}
